import { Button, Card, Form, FormProps, Space } from "antd";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BackButtonIcon from "../../../../assets/icons/BackButtonIcon";
import TopCard from "../../../../components/common/TopCard";
import EmployeeFileUpload from "../../../../components/Employees/EmployeeFileUpload";
import AppraisalKPIAddMoreForm from "../../../../components/KIP/AppraisalKPIAddMoreForm";
import KPIDetails from "../../../../components/KIP/KPIDetails";
import SubmitConfirmModal from "../../../../components/OwnAppraisals/SubmitConfirmModal";
import Loader from "../../../../components/ui/Loader";
import {
  useGetOwnSingleKpiQuery,
  useOwnAppraisalSubmitMutation,
} from "../../../../features/performanceManagement/performnceManagementApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { formatDate } from "../../../../lib/time";
import { toast } from "../../../../lib/toast";
import { findPlanVal } from "../OpenKPISetup/OpenKPISetup";
import {
  planPeriodList,
  planTypeList,
} from "../OpenKPISetup/OpenKPISetupCreate";

const backUrl = "/admin/kpi-setup/own-appraisals";

export default function OwnAppraisalsCreate() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { open, handleClose, handleOpen } = useModal<undefined, string>();
  const { id } = useParams();
  const { isLoading, isError, error, data } = useGetOwnSingleKpiQuery(
    String(id),
    {
      skip: !id,
    }
  );
  const [
    lineManagerSubmitKpi,
    {
      isLoading: isSubmitLoading,
      isError: isSubmitError,
      error: submitError,
      isSuccess: isSubmitSuccess,
    },
  ] = useOwnAppraisalSubmitMutation();
  useError(isSubmitError, submitError, "Own appraisal update fail");
  useError(isError, error, "Get appraisal fail");

  const kpiData = data?.data;

  const onFinish: FormProps<any>["onFinish"] = (values) => {
    const updates = values?.kpiArray?.map(
      ({ comment, ratingByOwn }: any, index: number) => ({
        index,
        comment,
        ratingByOwn,
      })
    );

    lineManagerSubmitKpi({
      id: Number(id),
      body: { updates, status: "pending" },
    });
  };

  useEffect(() => {
    if (id) {
      form.setFieldsValue({
        kpiArray: kpiData?.kpiArray,
        attachment: kpiData?.attachment,
      });
    }
  }, [form, id, kpiData?.attachment, kpiData?.kpiArray]);

  useEffect(() => {
    if (isSubmitSuccess) {
      toast("success", "Own appraisal updated successfully");
      handleClose();
      navigate(backUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccess, navigate]);

  const dueDate = kpiData?.kpi?.departments?.find(
    (el) => el?.departmentId === kpiData?.departmentId
  )?.due_date;

  return (
    <>
      <SidebarLayout title="Appraisal Report">
        <TopCard
          leftChildren={
            <>
              <Button
                className="normal-dark-button"
                icon={<BackButtonIcon />}
                onClick={() => navigate(-1)}
              >
                Back
              </Button>

              <div className="justify-start text-neutral-700 text-xl font-semibold">
                {kpiData?.kpi?.kpi_title}
              </div>
            </>
          }
        >
          {/* <Link to="">
            <Button
              type="primary"
              className="middle-button"
              icon={<SettingIcon />}
            />
          </Link> */}
        </TopCard>

        <div>
          {isLoading ? (
            <Loader />
          ) : (
            <Card>
              <KPIDetails
                department={kpiData?.department?.name}
                designation={kpiData?.designation}
                dueDate={dueDate ? formatDate(dueDate) : undefined}
                employeeId={kpiData?.employee?.employee_id}
                employeeName={kpiData?.employee?.member?.profile?.name}
                lineManageDesignation={kpiData?.lineManager?.role?.name}
                lineManageId={kpiData?.lineManager?.employee_id}
                lineManageName={kpiData?.lineManager?.member?.profile?.name}
                planPeriod={findPlanVal(
                  planPeriodList,
                  String(kpiData?.kpi?.plan_period)
                )}
                planType={findPlanVal(
                  planTypeList,
                  String(kpiData?.kpi?.plan_type)
                )}
                unit={kpiData?.unit}
              />
              <Form
                className="custom-form hide_star_mark"
                requiredMark="optional"
                layout="vertical"
                onFinish={onFinish}
                size="large"
                form={form}
              >
                <AppraisalKPIAddMoreForm
                  kpiArray="kpiArray"
                  kpiType="kpiType"
                  weighage="weightage"
                  details="details"
                  comment="comment"
                  ratingByOwn="ratingByOwn"
                  disabled={!!kpiData?.statusAppraisal}
                  disabledLineManagerInfo
                  hidLineManagerInfo={!kpiData?.statusAppraisal}
                />

                <div>
                  <div className="self-stretch px-3 py-1.5 bg-slate-100 rounded-md inline-flex justify-start items-center gap-2.5 w-full mb-4">
                    <div className="flex justify-start items-center gap-2.5">
                      <div className="justify-start text-slate-600 text-lg font-semibold">
                        Attachment
                      </div>
                    </div>
                  </div>

                  <div className="md:w-[75%]">
                    <Form.Item
                      name="attachment"
                      className="mb-4 md:w-1/2"
                      valuePropName="fileUrl"
                    >
                      <EmployeeFileUpload
                        form={form}
                        accept=".png, .jpg, .jpeg"
                        name="attachment"
                        onChange={(url) =>
                          form.setFieldsValue({
                            attachment: url,
                          })
                        }
                        disabled
                        showImage
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* <OwnOverallRating /> */}

                <Space className="w-full mt-4" size={12}>
                  {/* <Button
                  className="h-10 middle-button !border-display-light hover:!border-display-light !text-[#455468]"
                  icon={<DraftIcon />}
                >
                  Save as Draft
                </Button> */}
                  {!kpiData?.statusAppraisal ? (
                    <Button
                      className="modal-button"
                      type="primary"
                      size="middle"
                      htmlType="button"
                      onClick={() =>
                        handleOpen({
                          type: "CONFIRM",
                        })
                      }
                      disabled={isSubmitLoading}
                    >
                      Submit
                    </Button>
                  ) : null}
                </Space>
              </Form>
            </Card>
          )}
        </div>

        {open.type === "CONFIRM" && (
          <SubmitConfirmModal
            open={open.open}
            handleClose={handleClose}
            handleSubmit={() => form.submit()}
            isLoading={isSubmitLoading}
          />
        )}
      </SidebarLayout>
    </>
  );
}
